import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import * as api from "../api/status";
import { MonthlyHeatmap } from "../components/MonthlyHeatmap";
import { StatusTabs } from "../components/StatusTabs";
import { StatusTopBar } from "../components/StatusTopBar";

// We fetch a generous buffer client-side and let the user slide the
// 3-month window inside it without re-hitting the API.
const BUFFER_DAYS = 180;
const WINDOW_MONTHS = 3;

/** First day of the month, in UTC, offset by N months from `ref`. */
function monthStart(ref: Date, offset: number): Date {
  return new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth() + offset, 1));
}

/** Latest UTC midnight (today). */
function today(): Date {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

export function Uptime(): JSX.Element {
  const [data, setData] = useState<api.UptimeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serviceKey, setServiceKey] = useState<string>("__all__");
  // `monthsBack` shifts the visible 3-month window backwards: 0 = the
  // window ends with the current month, 1 = ends with last month, etc.
  const [monthsBack, setMonthsBack] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    void api
      .getUptime(BUFFER_DAYS)
      .then((r) => {
        if (!cancelled) { r.services = r.services.filter((s: any) => !s.key.includes("mp_") && !s.key.includes("marketplace")); setData(r); }
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolved visible months (oldest → newest), each as the 1st-of-month
  // UTC date used by MonthlyHeatmap.
  const visibleMonths = useMemo(() => {
    const ref = today();
    const last = monthStart(ref, -monthsBack);
    return Array.from({ length: WINDOW_MONTHS }, (_, i) =>
      monthStart(last, i - (WINDOW_MONTHS - 1)),
    );
  }, [monthsBack]);

  const periodLabel = useMemo(() => {
    if (visibleMonths.length === 0) return "";
    const first = visibleMonths[0]!;
    const last = visibleMonths[visibleMonths.length - 1]!;
    const fmt = (d: Date) =>
      d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    return `${fmt(first)} → ${fmt(last)}`;
  }, [visibleMonths]);

  // Aggregate days across services if "all" is selected; otherwise pick
  // the chosen service's series.
  const visibleDays = useMemo(() => {
    if (!data) return [];
    if (serviceKey === "__all__") {
      const merged = new Map<
        string,
        { total: number; ok: number }
      >();
      for (const s of data.services) {
        for (const d of s.days) {
          const cur = merged.get(d.date) ?? { total: 0, ok: 0 };
          cur.total += d.totalProbes;
          cur.ok += d.okProbes;
          merged.set(d.date, cur);
        }
      }
      return [...merged.entries()].map(([date, v]) => ({
        date,
        uptimePct: v.total === 0 ? 0 : (v.ok / v.total) * 100,
        totalProbes: v.total,
        okProbes: v.ok,
      }));
    }
    const s = data.services.find((x) => x.key === serviceKey);
    return s?.days ?? [];
  }, [data, serviceKey]);

  // Bucket days by `YYYY-MM` for fast lookup per heatmap.
  const daysByMonth = useMemo(() => {
    const m = new Map<string, typeof visibleDays>();
    for (const d of visibleDays) {
      const key = d.date.slice(0, 7);
      const arr = m.get(key) ?? [];
      arr.push(d);
      m.set(key, arr);
    }
    return m;
  }, [visibleDays]);

  // Disable "next" when already at the most recent window; "back" when
  // we'd be reading older than the buffer covers.
  const maxMonthsBack = Math.max(
    0,
    Math.floor(BUFFER_DAYS / 30) - WINDOW_MONTHS,
  );
  const canGoNewer = monthsBack > 0;
  const canGoOlder = monthsBack < maxMonthsBack;

  return (
    <main className="mx-auto max-w-6xl px-6 py-10">
      <StatusTopBar />
      <StatusTabs />

      {/* Filter row: service dropdown + period stepper. */}
      <section className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <select
          value={serviceKey}
          onChange={(e) => setServiceKey(e.target.value)}
          className="rounded-md border border-white/10 bg-dr-surface/60 px-3 py-2 text-sm text-dr-txt"
        >
          <option value="__all__">All services</option>
          {data?.services.map((s) => (
            <option key={s.key} value={s.key}>
              {s.displayName}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-3">
          <span className="text-sm text-text-secondary">{periodLabel}</span>
          <div className="flex overflow-hidden rounded-md border border-white/10">
            <button
              onClick={() => setMonthsBack((m) => Math.min(m + 1, maxMonthsBack))}
              disabled={!canGoOlder}
              aria-label="Previous period"
              className="px-3 py-1.5 text-sm text-text-secondary hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30"
            >
              ←
            </button>
            <div className="w-px bg-white/10" />
            <button
              onClick={() => setMonthsBack((m) => Math.max(m - 1, 0))}
              disabled={!canGoNewer}
              aria-label="Next period"
              className="px-3 py-1.5 text-sm text-text-secondary hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30"
            >
              →
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="card text-sm text-status-major">{error}</div>
      )}
      {!data && !error && (
        <p className="text-sm text-text-muted">Loading…</p>
      )}

      {data && (
        <div className="card p-7">
          <div className="grid gap-10 md:grid-cols-3">
            {visibleMonths.map((m) => {
              const key = m.toISOString().slice(0, 7);
              return (
                <MonthlyHeatmap
                  key={key}
                  month={m}
                  days={daysByMonth.get(key) ?? []}
                />
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] pt-4 text-xs text-text-muted">
            <Legend />
            <Link to="/" className="hover:text-text-secondary no-print">
              ← Back to overview
            </Link>
          </div>
        </div>
      )}

    </main>
  );
}

function Legend(): JSX.Element {
  const items: { label: string; color: string }[] = [
    { label: "Excellent", color: "#166534" },
    { label: "Good", color: "#4ade80" },
    { label: "Fair", color: "#86efac" },
    { label: "Partial", color: "#ca8a04" },
    { label: "Outage", color: "#ef4444" },
    { label: "No data / upcoming", color: "rgba(255,255,255,0.06)" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3">
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1.5">
          <span
            className="h-3 w-3 rounded-[3px] border border-white/10"
            style={{ background: i.color }}
            aria-hidden
          />
          {i.label}
        </span>
      ))}
    </div>
  );
}
